import { useCallback, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import type React from 'react';

import { Field, useField, useForm } from 'react-final-form';

import { translationKey } from '@change-corgi/core/i18n';
import { Radio, RadioGroup } from '@change-corgi/design-system-legacy/components/forms';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import { useFormI18n } from '#src/app/shared/form/hooks';
import { requiredValidator } from '#src/app/shared/form/validators';

import { useCategoryLabel } from '../hooks/useCategoryLabel';
import { getCategories } from '../shared/categories';
import type { Category, FormValues, ReportType } from '../shared/types';

type Props = Readonly<{
	type: ReportType;
	countryCode: string;
}>;

const validator = requiredValidator<Category>({
	i18n: { key: translationKey('fe.components.petition_report_abuse.errors.category_required') },
});

const RadioField: React.FC<{ type: ReportType; category: Category }> = ({ type, category }) => {
	const form = useForm();
	const { input } = useField<FormValues['category']>('category', {
		type: 'radio',
		value: category,
	});

	const getLabel = useCategoryLabel(type, 'category');

	// reset the subcategory when the category changes
	const onChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			form.change('subCategory', '');
			input.onChange(e);
		},
		[form, input],
	);

	return (
		<Box my={4}>
			<Radio
				id={`report-abuse-category-${category}`}
				label={getLabel(category)}
				value={input.value}
				name={input.name}
				checked={input.checked}
				onChange={onChange}
				data-testid={`reportAbuse-category-${category}-radio`}
				data-qa={`reportAbuse-category-${category}-radio`}
			/>
			<Box sx={{ display: input.checked ? 'block' : 'none' }} pl="m">
				<Text as="div" size="caption" color="typography-secondary">
					{getLabel(category, true)}
				</Text>
			</Box>
		</Box>
	);
};

export const CategoryField: React.FC<Props> = ({ type, countryCode }) => {
	const { getErrorMessage } = useFormI18n();

	const categories = useMemo(() => getCategories(type, countryCode), [type, countryCode]);

	return (
		<Box p={4}>
			<Field<FormValues['category']> name="category" subscription={{ error: true, touched: true }} validate={validator}>
				{({ meta }) => (
					// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
					<RadioGroup id="report-abuse-category" error={meta.touched && getErrorMessage(meta.error)}>
						{categories.map((category) => (
							<RadioField type={type} category={category} key={category} />
						))}
					</RadioGroup>
				)}
			</Field>
		</Box>
	);
};
